"use client";

import { memo, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { useAuiState } from "@assistant-ui/react";
import { BrainIcon, SearchIcon, ChevronDownIcon, GlobeIcon, FileTextIcon, TextSearchIcon, CheckCircle2Icon, XCircleIcon, BookOpenIcon, WrenchIcon, LoaderIcon, SaveIcon, PlusIcon, PencilIcon, RefreshCwIcon, GavelIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebugMode } from "@/components/custom/contexts/userSettingsContext";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { Spinner } from "@/components/ui/spinner";
import { McpServerIcon, hasMcpServerIcon } from "@/lib/mcpServerIcons";
import { useTranslation } from "react-i18next";
import { JsonPreview } from "@/components/custom/jsonPreview";
import { FRONTEND_INPUT_TOOL_NAMES, WEB_SEARCH_TOOL_NAMES, DOCUMENT_FLOW_TOOL_NAMES, HIDDEN_TOOL_NAMES, SUB_AGENT_TOOL_NAMES, type DocEditVariant, type DocEditPhaseKey } from "@/lib/toolConstants";
import type { UiPart, UiToolCallPart, ThinkingMetadata, SubAgentToolEntry, SubAgentLanePart } from "@/components/custom/chatModelAdapters";
import { parseStepPrefix } from "@/lib/stepParsing";
import { useDocumentEditFlow, type DocumentEditFlowState, type EditCycleState, type EditPhaseInfo } from "@/components/assistant-ui/document-stepper/useDocumentEditFlow";
import { findUnstableEntry } from "@/lib/assistantUiTypes";

// ─── Types ──────────────────────────────────────────────────────────────────────

type ReasoningStep = {
  heading: string;
  body: string;
  isLast: boolean;
};

type TimelineEntry =
  | {
      kind: "reasoning-step";
      key: string;
      heading: string;
      body: string;
      isLastStep: boolean;
      stepIndex: number;
    }
  | {
      kind: "tool-call";
      key: string;
      toolCallId: string;
      toolName: string;
      toolTitle?: string;
      argsText?: string;
      result?: unknown;
      mcpServerName?: string;
      progressMessage?: string;
      isRunning: boolean;
      status: "running" | "success" | "error";
      isWebSearch: boolean;
    }
  | {
      kind: "sub-agent";
      key: string;
      runId: string;
      query: string;
      agentType: string;
      locale: string;
      status: "running" | "completed" | "failed";
      description?: string;
      summary?: string;
      toolCalls: SubAgentToolEntry[];
    };

type ToolCallEntry = Extract<TimelineEntry, { kind: "tool-call" }>;
type SubAgentEntry = Extract<TimelineEntry, { kind: "sub-agent" }>;

type ToolGroup = {
  kind: "tool-group";
  key: string;
  groupKey: string;
  heading: string;
  runningText?: string;
  finishedText?: string;
  isWebSearch: boolean;
  isRunning: boolean;
  children: ToolCallEntry[];
};

type GroupedEntry =
  | Extract<TimelineEntry, { kind: "reasoning-step" }>
  | SubAgentEntry
  | ToolGroup;

type ServerConfig = {
  heading?: string;
  runningText?: string;
  finishedText?: string;
};

// ─── Constants ──────────────────────────────────────────────────────────────────

const EMPTY_CONTENT: readonly UiPart[] = [];
const EMPTY_PARTS: readonly RuntimePart[] = [];

function truncateLabel(text: string, max = 80): string {
  return text.length > max ? text.slice(0, max) + "…" : text;
}

// ─── Hoisted Icon Constants (stable references for memo) ────────────────────────

const ICON_SPINNER_SM = <Spinner size="sm" className="text-muted-foreground" />;
const ICON_SPINNER_MD = <Spinner size="sm" className="text-muted-foreground" />;
const ICON_BRAIN = <BrainIcon className="size-4 text-muted-foreground" />;
const ICON_DOT = <div className="size-2 rounded-full bg-muted-foreground/30" />;
const ICON_GLOBE = <GlobeIcon className="size-4 text-muted-foreground" />;
const ICON_BOOK = <BookOpenIcon className="size-4 text-muted-foreground" />;
const ICON_FILE = <FileTextIcon className="size-4 text-muted-foreground" />;
const ICON_DOCX = <img src="/docx-icon.png" alt="" className="size-4" />;
const ICON_SAVE = <SaveIcon className="size-4 text-muted-foreground" />;
const ICON_WRENCH = <WrenchIcon className="size-4 text-muted-foreground" />;
const ICON_GAVEL = <GavelIcon className="size-4 text-muted-foreground" />;
// Child / sub-timeline icons — smaller to create visual hierarchy
const ICON_CHECK_SUCCESS = <CheckCircle2Icon className="size-3 text-green-600/70" />;
const ICON_X_ERROR = <XCircleIcon className="size-3 text-muted-foreground" />;
const ICON_ACTIVE_PULSE = <LoaderIcon className="size-3 text-muted-foreground animate-spin" />;
// Parent-level variants (same icon, full size for top-level group headers)
const ICON_CHECK_SUCCESS_LG = <CheckCircle2Icon className="size-4 text-green-600/70" />;
const ICON_ACTIVE_PULSE_LG = <LoaderIcon className="size-4 text-muted-foreground animate-spin" />;

// ─── Elapsed Timer ──────────────────────────────────────────────────────────────

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min > 0 ? `${min}:${String(sec).padStart(2, "0")}` : `0:${String(sec).padStart(2, "0")}`;
}

const ElapsedTimer: FC<{
  startedAtUtc: string | null;
  totalDurationMs: number | null;
  isRunning: boolean;
}> = memo(({ startedAtUtc, totalDurationMs, isRunning }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (totalDurationMs != null) {
      setElapsed(totalDurationMs);
      return;
    }
    if (!startedAtUtc || !isRunning) return;

    const start = new Date(startedAtUtc).getTime();
    const tick = () => setElapsed(Date.now() - start);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAtUtc, totalDurationMs, isRunning]);

  if (elapsed === 0 && !isRunning) return null;

  return (
    <span className="text-xs tabular-nums text-muted-foreground/70 font-medium">
      {formatElapsed(elapsed)}
    </span>
  );
});
ElapsedTimer.displayName = "ElapsedTimer";

// ─── Performance: Throttled Selector Helpers ────────────────────────────────────

// assistant-ui runtime parts (from message.parts) carry a status sub-object
// that our adapter-level UiPart does not model. Minimal shape for type-safe access.
type RuntimePart = {
  type: string;
  toolCallId?: string;
  toolName?: string;
  state?: string;
  result?: unknown;
  status?: { type: string };
  progressMessage?: string;
};

type ProcessWindowSnapshot = {
  content: UiPart[];
  parts: readonly RuntimePart[];
  statusType: string | undefined;
  // Drives the "analyzing the query…" placeholder before any reasoning/tool
  // content arrives.
  isRunning: boolean;
  timingStartedAtUtc: string | null;
  timingTotalDurationMs: number | null;
  timelineSignature: string;
};

/** Count **bold** marker pairs in text without regex (fast O(n) scan). */
function countBoldPairs(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length - 1; i++) {
    if (text.charCodeAt(i) === 42 /* * */ && text.charCodeAt(i + 1) === 42) {
      count++;
      i++; // skip second *
    }
  }
  return count >>> 1;
}

function textTail(text: string | undefined, max = 120): string {
  if (!text) return "";
  return text.length <= max ? text : text.slice(-max);
}

function resultFingerprint(result: unknown): string {
  if (result == null) return "none";
  if (typeof result === "string") return `str:${result.length}:${textTail(result)}`;
  if (typeof result === "number" || typeof result === "boolean") return String(result);
  return "object";
}

function buildProcessWindowTimelineSignature(
  content: readonly UiPart[],
  parts: readonly RuntimePart[],
): string {
  const contentSignature = content.map((part, index) => {
    if (part.type === "reasoning") {
      const text = part.text ?? "";
      const taskTitle = part.metadata?.taskTitle ?? "";
      return `${index}:reasoning:${text.length}:${countBoldPairs(text)}:${textTail(text)}:${taskTitle}`;
    }

    if (part.type === "tool-call") {
      const tool = part as UiToolCallPart;
      return [
        index,
        "tool",
        tool.toolCallId,
        tool.toolName,
        tool.toolTitle,
        tool.state,
        tool.progressMessage,
        textTail(tool.argsText),
        resultFingerprint(tool.result),
        tool.mcpServerName,
      ].join(":");
    }

    if (part.type === "data") {
      const data = part as Partial<SubAgentLanePart> & { name?: string; status?: string };
      if (data.name === "sub-agent-lane") {
        const toolCalls = (data.toolCalls ?? [])
          .map((toolCall) => [
            toolCall.toolCallId,
            toolCall.toolName,
            toolCall.toolTitle,
            toolCall.status,
            toolCall.progressMessage,
          ].join(":"))
          .join(",");

        return [
          index,
          "sub-agent",
          data.runId,
          data.agentType,
          data.status,
          data.description,
          textTail(data.summary),
          toolCalls,
        ].join(":");
      }

      return `${index}:data:${data.name ?? ""}:${data.status ?? ""}`;
    }

    if (part.type === "text") {
      const text = "text" in part && typeof part.text === "string" ? part.text : "";
      return `${index}:text:${text.length}`;
    }

    return `${index}:unknown`;
  }).join("|");

  const partsSignature = parts.map((part, index) => [
    index,
    part.type,
    part.toolCallId,
    part.toolName,
    part.state,
    part.status?.type,
    part.progressMessage,
    resultFingerprint(part.result),
  ].join(":")).join("|");

  return `${contentSignature}#${partsSignature}`;
}

// ─── Reasoning Text Parser ──────────────────────────────────────────────────────

function parseReasoningSteps(text: string): ReasoningStep[] {
  if (!text) return [];

  const regex = /\*\*([^*]+)\*\*/g;
  const matches = [...text.matchAll(regex)];

  if (matches.length === 0) {
    return [{ heading: "", body: text.trim(), isLast: true }];
  }

  const steps: ReasoningStep[] = [];

  const preambleEnd = matches[0].index!;
  if (preambleEnd > 0) {
    const preamble = text.substring(0, preambleEnd).trim();
    if (preamble) {
      steps.push({ heading: "", body: preamble, isLast: false });
    }
  }

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const bodyStart = match.index! + match[0].length;
    const bodyEnd =
      i + 1 < matches.length ? matches[i + 1].index! : text.length;

    steps.push({
      heading: match[1],
      body: text.substring(bodyStart, bodyEnd).trim(),
      isLast: false,
    });
  }

  if (steps.length > 0) {
    steps[steps.length - 1].isLast = true;
  }

  return steps;
}

// ─── Grid Reveal ────────────────────────────────────────────────────────────────

function GridReveal({
  open,
  children,
  className,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
        className,
      )}
      style={{
        gridTemplateRows: open ? "1fr" : "0fr",
        opacity: open ? 1 : 0,
      }}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

// ─── Timeline Node (memoized) ───────────────────────────────────────────────────

const TimelineNode = memo(function TimelineNode({
  icon,
  label,
  sublabel,
  isActive,
  isLast,
  expandable,
  autoCollapse = false,
  defaultExpanded = false,
  animated = false,
  plain,
  nested,
  children,
}: {
  icon: ReactNode;
  label: ReactNode;
  sublabel?: string | null;
  isActive: boolean;
  isLast: boolean;
  expandable?: boolean;
  autoCollapse?: boolean;
  defaultExpanded?: boolean;
  animated?: boolean;
  plain?: boolean;
  nested?: boolean;
  children?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const manualToggleRef = useRef(false);

  // Auto-expand when active, auto-collapse when inactive (unless manually toggled)
  useEffect(() => {
    if (!autoCollapse) return;
    if (manualToggleRef.current) return;
    setExpanded(isActive);
  }, [isActive, autoCollapse]);

  return (
    <div className={cn(
      "relative",
      animated && "animate-node-enter",
    )}>
      {/* Vertical connector line */}
      <div
        className="absolute left-[10px] top-[22px] bottom-0 w-px bg-[#e8e5e1] dark:bg-border origin-top"
        style={{
          transform: isLast ? "scaleY(0)" : "scaleY(1)",
          transition: animated ? "transform 300ms ease-out" : "none",
        }}
      />

      <div className={cn("pb-3", isLast && "pb-1")}>
        <div
          className={cn(
            "flex items-center gap-2 font-normal leading-tight",
            plain ? "text-[11.5px]" : "text-[13px]",
            expandable && "cursor-pointer select-none hover:text-foreground",
            isActive ? "text-muted-foreground" : "text-muted-foreground/70",
          )}
          onClick={expandable ? () => { manualToggleRef.current = true; setExpanded(!expanded); } : undefined}
        >
          <div className="flex size-[22px] shrink-0 items-center justify-center">
            {icon}
          </div>
          <span className="flex items-center gap-2">{label}</span>
          {expandable && (
            <ChevronDownIcon
              className={cn(
                "size-3 shrink-0 text-muted-foreground transition-transform duration-150",
                expanded && "rotate-180",
              )}
            />
          )}
        </div>
        {sublabel && (
          <p className="ml-[30px] text-[11px] text-muted-foreground/70 mt-1 leading-relaxed">
            {sublabel}
          </p>
        )}
        <GridReveal open={!!(expandable && expanded && children)}>
          <div className={cn(
            "ml-[30px] mt-2",
            nested
              ? ""
              : "max-h-48 overflow-y-auto text-xs text-muted-foreground/80 whitespace-pre-wrap leading-relaxed",
          )}>
            {children}
          </div>
        </GridReveal>
      </div>
    </div>
  );
});

// ─── Web Search Action Icon (memoized) ──────────────────────────────────────────

/** Extract domain from argsText like "example.com | slug text" */
function extractDomain(text?: string): string | null {
  if (!text) return null;
  const part = text.split(" | ")[0].trim();
  // Basic domain check: contains a dot, no spaces
  if (part.includes(".") && !part.includes(" ")) return part;
  return null;
}

const WebSearchIcon = memo(function WebSearchIcon({ toolName, argsText }: { toolName: string; argsText?: string }) {
  const [faviconFailed, setFaviconFailed] = useState(false);
  const domain = extractDomain(argsText);

  if (domain && !faviconFailed) {
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`}
        alt=""
        className="size-3.5 shrink-0 rounded-sm object-contain"
        onError={() => setFaviconFailed(true)}
      />
    );
  }

  switch (toolName) {
    case "web_search_query":
      return <SearchIcon className="size-3.5 shrink-0 text-blue-500" />;
    case "web_open_page":
      return <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />;
    case "web_find_in_page":
      return <TextSearchIcon className="size-3.5 shrink-0 text-amber-500" />;
    default:
      return <GlobeIcon className="size-3.5 shrink-0 text-blue-500" />;
  }
});

// ─── Tool Call Detail (memoized, single JSON parse) ─────────────────────────────

const ToolCallDetail = memo(function ToolCallDetail({ argsText, result }: { argsText?: string; result?: unknown }) {
  // Raw arguments and tool results are DEBUG data — never surfaced to end users.
  // ProcessWindow is the clean (non-debug) timeline and only renders when
  // !debugMode, but this guard is the single source of truth so the component is
  // self-protecting if ever reused. Debug users see the full trace via
  // tool-fallback.tsx's ToolGroup.
  const { debugMode } = useDebugMode();
  const hasArgs = !!argsText && argsText.length > 0;
  const hasResult = result != null && result !== "";

  const parsedArgs = useMemo(() => {
    if (!hasArgs) return undefined;
    try { return JSON.parse(argsText!); } catch { return undefined; }
  }, [argsText, hasArgs]);

  const resultStr = useMemo(
    () => !hasResult ? undefined : typeof result === "string" ? result : JSON.stringify(result, null, 2),
    [result, hasResult],
  );

  const parsedResult = useMemo(() => {
    if (!resultStr) return undefined;
    try { return JSON.parse(resultStr); } catch { return undefined; }
  }, [resultStr]);

  if (!debugMode) return null;
  if (!hasArgs && !hasResult) return null;

  return (
    <div className="space-y-2">
      {hasArgs && (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground mb-1">Args</p>
          {parsedArgs !== undefined ? (
            <JsonPreview value={parsedArgs} />
          ) : (
            <div className="whitespace-pre-wrap text-[11px] text-muted-foreground border rounded bg-muted/50 px-2 py-1">
              {argsText}
            </div>
          )}
        </div>
      )}
      {hasResult && resultStr && (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground mb-1">Result</p>
          {parsedResult !== undefined ? (
            <JsonPreview value={parsedResult} />
          ) : (
            <div className="whitespace-pre-wrap text-[11px] text-muted-foreground border rounded bg-muted/50 px-2 py-1">
              {resultStr}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ─── Sub-Agent Tool Call Row (expandable detail) ─────────────────────────────────

function SubAgentToolCallRow({ tc }: { tc: SubAgentToolEntry }) {
  const { debugMode } = useDebugMode();
  const [expanded, setExpanded] = useState(false);
  // Args/result are debug-only data — no expand affordance in the production view.
  const hasDetail = debugMode && !!(tc.argsText || tc.result);
  const tcIcon = tc.status === "running" ? ICON_ACTIVE_PULSE : tc.status === "success" ? ICON_CHECK_SUCCESS : ICON_X_ERROR;
  const tcLabel = tc.toolTitle ?? tc.toolName;

  return (
    <div className="py-0.5">
      <div
        className={cn(
          "flex items-center gap-1.5 text-[11.5px] text-muted-foreground/70",
          hasDetail && "cursor-pointer hover:text-muted-foreground",
        )}
        onClick={hasDetail ? () => setExpanded(!expanded) : undefined}
      >
        {tcIcon}
        <span className="flex-1 truncate">{tcLabel}</span>
        {tc.progressMessage && (
          <span className="ml-auto text-[10px] text-muted-foreground/50 truncate max-w-[120px]">
            {tc.progressMessage}
          </span>
        )}
        {hasDetail && (
          <ChevronDownIcon className={cn("size-3 text-muted-foreground/50 transition-transform", expanded && "rotate-180")} />
        )}
      </div>
      {expanded && hasDetail && (
        <div className="ml-4 mt-1 mb-1">
          <ToolCallDetail argsText={tc.argsText} result={tc.result} />
        </div>
      )}
    </div>
  );
}

// ─── Sub-Agent Lane Component ────────────────────────────────────────────────────

const SubAgentLaneNode = memo(function SubAgentLaneNode({
  entry,
  isLast,
  isLiveMode,
  t,
}: {
  entry: SubAgentEntry;
  isLast: boolean;
  isLiveMode: boolean;
  t: (key: string) => string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  // Auto-scroll to bottom when new tool calls arrive (unless user scrolled up).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || userScrolledRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [entry.toolCalls.length]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 24;
    userScrolledRef.current = el.scrollTop + el.clientHeight < el.scrollHeight - threshold;
  };

  const isRunning = entry.status === "running";
  // Use ASPI icon for ASPI research lanes regardless of status; generic spinner/check for others.
  const isAspi = entry.agentType?.startsWith("aspi-research");
  const aspiIcon = <McpServerIcon name="aspiCz" className="size-4 rounded-sm object-contain shrink-0" />;
  const icon = isAspi
    ? aspiIcon
    : isRunning
      ? ICON_ACTIVE_PULSE_LG
      : entry.status === "completed"
        ? ICON_CHECK_SUCCESS_LG
        : ICON_X_ERROR;
  const label = isRunning
    ? (entry.description ?? t("subAgent.research.running"))
    : entry.status === "completed"
      ? t("subAgent.research.completed")
      : t("subAgent.research.failed");

  return (
    <div className={cn("relative", isLiveMode && "animate-node-enter")}>
      {/* Vertical connector */}
      <div
        className="absolute left-[10px] top-[22px] bottom-0 w-px bg-[#e8e5e1] dark:bg-border origin-top"
        style={{ transform: isLast ? "scaleY(0)" : "scaleY(1)" }}
      />
      <div className={cn("pb-3", isLast && "pb-1")}>
        {/* Header row */}
        <div className="flex items-center gap-2 text-[13px] font-normal leading-tight text-muted-foreground">
          <div className="flex size-[22px] shrink-0 items-center justify-center">
            {icon}
          </div>
          <span className="flex items-center gap-1.5">
            <TextSearchIcon className="size-3.5 text-muted-foreground/70" />
            {label}
          </span>
        </div>

        {/* Inner tool calls — scrollable viewport with max 4 visible */}
        {entry.toolCalls.length > 0 && (
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="ml-[22px] mt-1.5 max-h-32 overflow-y-auto border-l border-border/50 pl-3"
          >
            {entry.toolCalls.map((tc) => (
              <SubAgentToolCallRow key={tc.toolCallId} tc={tc} />
            ))}
          </div>
        )}

        {/* Completion summary */}
        {!isRunning && entry.summary && (
          <div className="ml-[22px] mt-1.5 pl-3 text-[11.5px] text-muted-foreground/60 leading-snug">
            {entry.summary}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Unified Timeline Builder ────────────────────────────────────────────────────

function buildUnifiedTimeline(
  content: UiPart[],
  parts: readonly RuntimePart[],
  messageIsTerminal: boolean,
): TimelineEntry[] {
  // Build status map from parts for live tool-call status
  const toolStatusMap = new Map<string, RuntimePart>();
  for (const part of parts) {
    if (part.type === "tool-call" && part.toolCallId) {
      toolStatusMap.set(part.toolCallId, part);
    }
  }

  // Find the last reasoning part index (for active-step detection)
  let lastReasoningPartIdx = -1;
  for (let ci = content.length - 1; ci >= 0; ci--) {
    if (content[ci].type === "reasoning") {
      lastReasoningPartIdx = ci;
      break;
    }
  }

  const timeline: TimelineEntry[] = [];
  let reasoningStepCounter = 0;

  for (let ci = 0; ci < content.length; ci++) {
    const part = content[ci];

    if (part.type === "reasoning") {
      const isLastReasoningPart = ci === lastReasoningPartIdx;

      // New narration format: part has stepLabel/stepIndex from data-step-label event
      // or from [STEP:N:label] prefix parsing in history. Use directly.
      // Always strip [STEP:N:label] prefix from the raw text — it may arrive
      // before the data-step-label event during streaming, causing a brief flash.
      const { stepLabel: parsedLabel, stepIndex: parsedIndex, bodyText } = parseStepPrefix(part.text ?? "");
      const effectiveLabel = part.stepLabel ?? parsedLabel;
      const effectiveIndex = part.stepIndex ?? parsedIndex;

      if (effectiveLabel) {
        reasoningStepCounter++;
        timeline.push({
          kind: "reasoning-step",
          key: `reasoning-${ci}-step-0`,
          heading: effectiveLabel,
          body: bodyText,
          isLastStep: isLastReasoningPart,
          stepIndex: effectiveIndex ?? reasoningStepCounter,
        });
      } else {
        // Legacy format: split on **bold headings**
        const steps = parseReasoningSteps(bodyText);
        for (let si = 0; si < steps.length; si++) {
          reasoningStepCounter++;
          timeline.push({
            kind: "reasoning-step",
            key: `reasoning-${ci}-step-${si}`,
            heading: steps[si].heading,
            body: steps[si].body,
            isLastStep: isLastReasoningPart && steps[si].isLast,
            stepIndex: reasoningStepCounter,
          });
        }
      }
    } else if (part.type === "tool-call") {
      // Frontend-input tools render their own interactive card via the `tools:` map in
      // thread.tsx; omit them from the Analysis timeline so the user doesn't see a
      // spinner+args trace next to (or instead of) the card they're supposed to fill in.
      // Exception: request_commit_approval shows a localized "awaiting approval" node.
      if (FRONTEND_INPUT_TOOL_NAMES.has(part.toolName ?? "") && part.toolName !== "request_commit_approval") continue;
      // Hidden tools are internal housekeeping — never shown to the user.
      if (HIDDEN_TOOL_NAMES.has(part.toolName ?? "")) continue;
      // Document-flow tools are rendered by the DocumentFlowGroup section below;
      // skip them from the generic timeline to avoid duplication.
      if (DOCUMENT_FLOW_TOOL_NAMES.has(part.toolName ?? "")) continue;
      // Sub-agent orchestration tools render via their sub-agent-lane part.
      if (SUB_AGENT_TOOL_NAMES.has(part.toolName ?? "")) continue;
      // Orphan tool-call parts with no real toolName (SSE ordering artifacts or
      // legacy null-FunctionName DB rows) — hide them from the timeline.
      if (!part.toolName || part.toolName === "tool") continue;

      const toolCallId = part.toolCallId ?? `tool-${ci}`;
      const livePart = toolStatusMap.get(toolCallId);
      const isRunning =
        !messageIsTerminal && (
        part.state === "partial" ||
        part.state === "pending" ||
        livePart?.status?.type === "running" ||
        livePart?.state === "partial" ||
        livePart?.state === "pending");

      const isError =
        !isRunning &&
        livePart?.status?.type === "incomplete";

      const status: "running" | "success" | "error" =
        isRunning ? "running" : isError ? "error" : "success";

      timeline.push({
        kind: "tool-call",
        key: toolCallId,
        toolCallId,
        toolName: part.toolName ?? "tool",
        toolTitle: part.toolTitle,
        argsText: part.argsText,
        result: part.result ?? livePart?.result,
        mcpServerName: part.mcpServerName,
        progressMessage: part.progressMessage ?? livePart?.progressMessage,
        isRunning,
        status,
        isWebSearch: WEB_SEARCH_TOOL_NAMES.has(part.toolName ?? ""),
      });
    } else if (part.type === "data" && (part as SubAgentLanePart).name === "sub-agent-lane") {
      const lane = part as SubAgentLanePart;
      timeline.push({
        kind: "sub-agent",
        key: `sub-agent-${lane.runId}`,
        runId: lane.runId,
        query: lane.query,
        agentType: lane.agentType,
        locale: lane.locale,
        status: lane.status,
        description: lane.description,
        summary: lane.summary,
        toolCalls: lane.toolCalls,
      });
    }
    // type "text" → skip
  }

  return timeline;
}

// ─── Group Consecutive Tool Calls ─────────────────────────────────────────────

function groupTimeline(
  flat: TimelineEntry[],
  serverConfigMap: Map<string, ServerConfig>,
  t: (key: string) => string,
): GroupedEntry[] {
  const result: GroupedEntry[] = [];
  let current: ToolGroup | null = null;

  for (const entry of flat) {
    if (entry.kind === "reasoning-step" || entry.kind === "sub-agent") {
      if (current) { result.push(current); current = null; }
      result.push(entry);
      continue;
    }
    const gk = entry.isWebSearch ? "__web_search__" : entry.toolName === "request_commit_approval" ? "__approval__" : (entry.mcpServerName || "__other__");
    if (current && current.groupKey === gk) {
      current.children.push(entry);
      if (entry.isRunning) current.isRunning = true;
    } else {
      if (current) result.push(current);
      const cfg = serverConfigMap.get(entry.mcpServerName || "");
      current = {
        kind: "tool-group",
        key: `group-${gk}-${result.length}`,
        groupKey: gk,
        heading: entry.isWebSearch
          ? t("tools.webSearchDone")
          : (cfg?.heading || (t(`${gk}.toolGroupHeading`) !== `${gk}.toolGroupHeading` ? t(`${gk}.toolGroupHeading`) : null) || entry.mcpServerName || t("tools.otherTools")),
        runningText: entry.isWebSearch ? t("tools.webSearchRunning") : (cfg?.runningText || (t(`${gk}.toolGroupRunningText`) !== `${gk}.toolGroupRunningText` ? t(`${gk}.toolGroupRunningText`) : null) || undefined),
        finishedText: entry.isWebSearch ? t("tools.webSearchDone") : (cfg?.finishedText || (t(`${gk}.toolGroupFinishedText`) !== `${gk}.toolGroupFinishedText` ? t(`${gk}.toolGroupFinishedText`) : null) || undefined),
        isWebSearch: entry.isWebSearch,
        isRunning: entry.isRunning,
        children: [entry],
      };
    }
  }
  if (current) result.push(current);
  return result;
}

// ─── Grouped Timeline Cache ─────────────────────────────────────────────────────
// Reuses the same object reference for grouped entries whose content hasn't changed,
// so memo'd TimelineNode children bail out on unchanged entries.

function isToolCallEqual(a: ToolCallEntry, b: ToolCallEntry): boolean {
  return a.status === b.status
    && a.progressMessage === b.progressMessage
    && a.isRunning === b.isRunning
    && (a.result != null) === (b.result != null);
}

function isGroupedEntryEqual(a: GroupedEntry, b: GroupedEntry): boolean {
  if (a.kind !== b.kind) return false;

  if (a.kind === "reasoning-step" && b.kind === "reasoning-step") {
    return a.heading === b.heading
      && a.body === b.body
      && a.isLastStep === b.isLastStep;
  }

  if (a.kind === "sub-agent" && b.kind === "sub-agent") {
    if (a.status !== b.status) return false;
    if (a.summary !== b.summary) return false;
    if (a.description !== b.description) return false;
    if (a.toolCalls.length !== b.toolCalls.length) return false;
    for (let i = 0; i < a.toolCalls.length; i++) {
      if (a.toolCalls[i].status !== b.toolCalls[i].status) return false;
      if (a.toolCalls[i].progressMessage !== b.toolCalls[i].progressMessage) return false;
    }
    return true;
  }

  // tool-group
  if (a.kind === "tool-group" && b.kind === "tool-group") {
    if (a.isRunning !== b.isRunning) return false;
    if (a.children.length !== b.children.length) return false;
    for (let i = 0; i < a.children.length; i++) {
      if (!isToolCallEqual(a.children[i], b.children[i])) return false;
    }
    return true;
  }

  return false;
}

function useGroupedTimelineCache(groupedTimeline: GroupedEntry[]): GroupedEntry[] {
  const cacheRef = useRef<Map<string, GroupedEntry>>(new Map());

  return useMemo(() => {
    const nextCache = new Map<string, GroupedEntry>();
    const result: GroupedEntry[] = [];

    for (const entry of groupedTimeline) {
      const cached = cacheRef.current.get(entry.key);
      if (cached && isGroupedEntryEqual(cached, entry)) {
        nextCache.set(entry.key, cached);
        result.push(cached);
      } else {
        nextCache.set(entry.key, entry);
        result.push(entry);
      }
    }

    cacheRef.current = nextCache;
    return result;
  }, [groupedTimeline]);
}

// ─── Native Tool Icon Resolution ────────────────────────────────────────────────

const NATIVE_TOOL_ICON_PATTERNS: [RegExp, ReactNode][] = [
  [/^read_frontend_input_skill$|^read_document_generation_skill$/, ICON_BOOK],
  [/^read_legal_research_skill$/, ICON_GAVEL],
  [/^request_commit_approval$/, ICON_SAVE],
  [/^show_generated_document$|^show_praetor_document$/, ICON_DOCX],
  [/^save_to_thread_generated_documents$|^draft_document$|^draft_new_document$|^revise_generated_document$|^revise_praetor_document$|^commit_draft$|^save_revision$/, ICON_FILE],
];

function resolveNativeGroupIcon(group: ToolGroup): ReactNode {
  for (const child of group.children) {
    for (const [pattern, icon] of NATIVE_TOOL_ICON_PATTERNS) {
      if (pattern.test(child.toolName)) return icon;
    }
  }
  return group.groupKey === "__other__" ? ICON_WRENCH : ICON_DOT;
}

// ─── Document Edit Flow Phase Labels (variant-aware) ────────────────────────────

function getDocEditPhaseLabel(
  t: (key: string) => string,
  variant: DocEditVariant,
  phase: DocEditPhaseKey,
  status: "active" | "done" | "pending",
): string {
  // "reviewing" and "saving" are variant-agnostic
  if (phase === "reviewing") {
    return status === "done" ? t("docEdit.reviewing.done") : t("docEdit.reviewing.active");
  }
  if (phase === "saving") {
    return status === "done" ? t("docEdit.saving.done") : t("docEdit.saving.active");
  }

  // Variant-specific keys for "preparing" and "editing"
  const variantKey = variant === "template" ? "template"
    : variant === "case-edit" ? "caseEdit"
    : "reEdit";
  const statusKey = status === "done" ? "done" : "active";
  return t(`docEdit.${phase}.${statusKey}.${variantKey}`);
}

function getDocEditCycleTitle(t: (key: string, opts?: object) => string, variant: DocEditVariant, documentName?: string): string {
  const variantKey = variant === "template" ? "template"
    : variant === "case-edit" ? "caseEdit"
    : "reEdit";
  if (documentName) {
    return t(`docEdit.title.${variantKey}Named`, { name: documentName });
  }
  return t(`docEdit.title.${variantKey}`);
}

function getDocEditCompleteTitle(t: (key: string, opts?: object) => string, variant: DocEditVariant, documentName?: string): string {
  const variantKey = variant === "template" ? "template"
    : variant === "case-edit" ? "caseEdit"
    : "reEdit";
  if (documentName) {
    return t(`docEdit.complete.${variantKey}Named`, { name: documentName });
  }
  return t(`docEdit.complete.${variantKey}`);
}

// ─── Document Edit Cycle Icon (DOCX + badge) ────────────────────────────────────

function DocEditCycleIcon({ variant }: { variant: DocEditVariant }) {
  const badge = variant === "template"
    ? <PlusIcon className="size-1.5 text-muted-foreground" />
    : variant === "re-edit"
      ? <RefreshCwIcon className="size-1.5 text-muted-foreground" />
      : <PencilIcon className="size-1.5 text-muted-foreground" />;

  return (
    <span className="relative inline-flex items-center justify-center">
      <img src="/docx-icon.png" alt="" className="size-4 shrink-0 object-contain" />
      <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-background border border-border flex items-center justify-center">
        {badge}
      </span>
    </span>
  );
}

// ─── Document Edit Flow Group (renders one or more edit cycles) ──────────────────

const DocumentEditFlowGroup = memo(function DocumentEditFlowGroup({
  editFlow,
  isLiveMode,
}: {
  editFlow: DocumentEditFlowState;
  isLiveMode: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      {editFlow.cycles.map((cycle, cycleIdx) => {
        const isLastCycle = cycleIdx === editFlow.cycles.length - 1;
        const cycleLabel = getDocEditCycleTitle(t, cycle.variant, cycle.documentName);
        const retryBadge = cycle.retryCount > 0
          ? ` (${t("docEdit.retryBadge", { count: cycle.retryCount + 1 })})`
          : "";

        const headerLabel = cycle.isComplete
          ? getDocEditCompleteTitle(t, cycle.variant, cycle.documentName)
          : cycle.hasError
            ? t("docEdit.error")
            : `${cycleLabel}${retryBadge}`;

        const groupIcon = cycle.hasError
          ? ICON_X_ERROR
          : <DocEditCycleIcon variant={cycle.variant} />;

        // Progressive reveal: during live mode, only show phases that are
        // done/active/error plus the next pending phase as a "coming next" hint.
        const visiblePhases = cycle.isComplete || !isLiveMode
          ? cycle.phases
          : cycle.phases.filter((p, idx) => {
              if (p.status !== "pending") return true;
              const prev = idx > 0 ? cycle.phases[idx - 1] : null;
              return prev != null && prev.status !== "pending";
            });

        if (cycle.isAbandoned) {
          return (
            <TimelineNode
              key={cycle.editId}
              icon={ICON_DOT}
              label={t("docEdit.abandoned")}
              isActive={false}
              isLast={isLastCycle}
              plain
            />
          );
        }

        return (
          <TimelineNode
            key={cycle.editId}
            icon={groupIcon}
            label={headerLabel}
            isActive={cycle.isRunning || (isLiveMode && !cycle.isComplete)}
            isLast={isLastCycle}
            expandable
            defaultExpanded={isLiveMode}
            animated={isLiveMode}
            nested
            plain
          >
            {visiblePhases.map((phase, idx) => {
              const isLastVisible = idx === visiblePhases.length - 1;
              const showAsLoading = isLiveMode && !cycle.isComplete && isLastVisible && phase.status === "pending";

              const phaseIcon =
                phase.status === "error" ? ICON_X_ERROR :
                phase.status === "done" ? ICON_CHECK_SUCCESS :
                (phase.status === "active" || showAsLoading) ? ICON_ACTIVE_PULSE :
                ICON_DOT;

              const effectiveStatus = showAsLoading ? "active" as const : phase.status === "error" ? "active" as const : phase.status;
              const label = getDocEditPhaseLabel(t, cycle.variant, phase.key, effectiveStatus === "pending" ? "pending" : effectiveStatus === "done" ? "done" : "active");

              return (
                <TimelineNode
                  key={phase.key}
                  icon={phaseIcon}
                  label={label}
                  sublabel={(phase.status === "active" || phase.status === "done") ? phase.subLabel : undefined}
                  isActive={phase.status === "active" || showAsLoading}
                  isLast={isLastVisible}
                  animated={isLiveMode}
                  plain
                />
              );
            })}
            {cycle.isComplete && cycle.summary && (
              <div className="ml-6 mt-1 mb-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
                {cycle.summary}
              </div>
            )}
          </TimelineNode>
        );
      })}
    </>
  );
});


// ─── Process Window ─────────────────────────────────────────────────────────────

const ProcessWindowImpl: FC = () => {
  const { t } = useTranslation();
  const { debugMode } = useDebugMode();
  const { allServers } = useMcpServers();

  // ── Deterministic combined selector ──
  // useAuiState is backed by useSyncExternalStore, so the selector must return
  // the same object for the same store state. Do not use timers/randomness here.
  const snapshotCacheRef = useRef<ProcessWindowSnapshot | null>(null);

  const { content, parts, statusType, isRunning, timingStartedAtUtc, timingTotalDurationMs, timelineSignature } = useAuiState(({ message, thread }) => {
    const rawContent = (message.content ?? EMPTY_CONTENT) as unknown as UiPart[];
    const rawParts = (message.parts ?? EMPTY_PARTS) as readonly RuntimePart[];
    const timing = findUnstableEntry(message.metadata?.unstable_data, "timing");
    const nextStatusType = message.status?.type;
    const nextIsRunning = Boolean(thread?.isRunning);
    const nextTimingStartedAtUtc = timing?.startedAtUtc ?? null;
    const nextTimingTotalDurationMs = timing?.totalDurationMs ?? null;
    const nextTimelineSignature = buildProcessWindowTimelineSignature(rawContent, rawParts);

    const cached = snapshotCacheRef.current;
    if (
      cached &&
      cached.statusType === nextStatusType &&
      cached.isRunning === nextIsRunning &&
      cached.timingStartedAtUtc === nextTimingStartedAtUtc &&
      cached.timingTotalDurationMs === nextTimingTotalDurationMs &&
      cached.timelineSignature === nextTimelineSignature
    ) {
      return cached;
    }

    const next: ProcessWindowSnapshot = {
      content: rawContent,
      parts: rawParts,
      statusType: nextStatusType,
      isRunning: nextIsRunning,
      timingStartedAtUtc: nextTimingStartedAtUtc,
      timingTotalDurationMs: nextTimingTotalDurationMs,
      timelineSignature: nextTimelineSignature,
    };

    snapshotCacheRef.current = next;
    return next;
  });

  const messageIsTerminal = statusType != null && statusType !== "running";

  // Per-message timer gate: only tick the timer when THIS specific message is
  // actively streaming. Without this, all ProcessWindow instances on the thread
  // would tick because `isRunning` is a thread-level flag — old cancelled/completed
  // messages would show absurd elapsed times (e.g. "5198:51").
  const timerIsRunning = isRunning && !messageIsTerminal;

  const timeline = useMemo(
    () => buildUnifiedTimeline(content, parts, messageIsTerminal),
    [content, parts, messageIsTerminal, timelineSignature],
  );

  const flags = useMemo(() => {
    let hasToolCalls = false;
    let isToolsRunning = false;
    let isReasoningStreaming = false;

    for (const entry of timeline) {
      if (entry.kind === "tool-call") {
        hasToolCalls = true;
        if (entry.isRunning) isToolsRunning = true;
      }
    }

    if (statusType === "running" && content.length > 0) {
      const lastPart = content[content.length - 1];
      if (lastPart?.type === "reasoning") isReasoningStreaming = true;
    }

    return { hasToolCalls, isToolsRunning, isReasoningStreaming };
  }, [timeline, statusType, content]);

  const serverConfigMap = useMemo(() => {
    const map = new Map<string, ServerConfig>();
    for (const s of allServers) {
      map.set(s.name, {
        heading: t(`${s.name}.toolGroupHeading`),
        runningText: t(`${s.name}.toolGroupRunningText`),
        finishedText: t(`${s.name}.toolGroupFinishedText`),
      });
    }
    return map;
  }, [allServers, t]);

  // Track whether this message has ever been seen as "running" in this mount.
  // Once latched to true, it stays true (so the UI doesn't collapse on completion).
  // History messages (loaded with status "complete") start collapsed; live messages
  // start expanded. When a history message resumes running, we switch to live mode.
  const [isLiveMode, setIsLiveMode] = useState(statusType === "running");
  const [expanded, setExpanded] = useState(statusType === "running");
  // Generation counter: incremented when switching from history→live to force
  // remount of TimelineNode components (so they pick up new defaultExpanded).
  const [timelineGen, setTimelineGen] = useState(0);

  useEffect(() => {
    if (statusType === "running" && !isLiveMode) {
      setIsLiveMode(true);
      setExpanded(true);
      setTimelineGen(g => g + 1);
    } else if (statusType != null && statusType !== "running" && isLiveMode) {
      // Message became terminal (cancelled, complete, incomplete) — stop live spinners.
      setIsLiveMode(false);
    }
  }, [statusType, isLiveMode]);

  const rawGroupedTimeline = useMemo(
    () => groupTimeline(timeline, serverConfigMap, t),
    [timeline, serverConfigMap, t],
  );
  const groupedTimeline = useGroupedTimelineCache(rawGroupedTimeline);

  // Extract metadata from the first reasoning part that carries one.
  const metadata = useMemo(() => {
    for (const part of content) {
      if (part.type === "reasoning" && part.metadata) {
        return part.metadata;
      }
    }
    return null as ThinkingMetadata | null;
  }, [content, timelineSignature]);

  // ── Document flow detection ──
  const docEditFlow = useDocumentEditFlow();

  if (debugMode) return null;

  // While the turn is in flight but no reasoning/tool-call content has landed yet,
  // render the placeholder branch below (analysis shell + "analyzing the query…"
  // timeline node). Hide entirely once the run has ended with no entries — e.g.
  // a text-only turn or an aborted send before any event arrived.
  if (timeline.length === 0 && !docEditFlow.isActive && !isRunning) return null;

  if (timeline.length === 0 && !docEditFlow.isActive && isRunning) {
    return (
      <div className="aui-process-window aui-analyzing px-1 py-2 mb-3">
        <div className="flex items-center gap-2 pb-3 select-none">
          <ElapsedTimer
            startedAtUtc={timingStartedAtUtc}
            totalDurationMs={timingTotalDurationMs}
            isRunning={timerIsRunning}
          />
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
            {t("reasoning.analysis")}
          </span>
        </div>
        <TimelineNode
          icon={ICON_ACTIVE_PULSE}
          label={t("assistant.thinkingIndicator")}
          isActive
          isLast
          plain
        />
      </div>
    );
  }

  const isAnyActive = flags.isToolsRunning || flags.isReasoningStreaming || docEditFlow.cycles.some(c => c.isRunning);

  return (
    <div className={cn(
      "aui-process-window px-1 py-2 mb-3",
      flags.hasToolCalls && "aui-has-tool-calls",
      flags.isToolsRunning && "aui-tools-running",
      flags.isReasoningStreaming && "aui-reasoning-active",
    )}>
      {/* Analysis Header */}
      <div
        className="flex items-center gap-2 pb-3 cursor-pointer select-none hover:text-foreground"
        onClick={() => setExpanded((v) => !v)}
      >
        <ElapsedTimer
          startedAtUtc={timingStartedAtUtc}
          totalDurationMs={timingTotalDurationMs}
          isRunning={timerIsRunning}
        />
        {metadata?.taskTitle ? (
          <span className={cn(
            "text-xs font-semibold uppercase tracking-[0.12em] animate-in fade-in duration-300",
            isAnyActive ? "text-foreground" : "text-muted-foreground",
          )}>
            {metadata.taskTitle}
          </span>
        ) : (
          <span className={cn(
            "text-xs font-semibold uppercase tracking-[0.12em]",
            isAnyActive ? "text-foreground" : "text-muted-foreground",
          )}>
            {t("reasoning.analysis")}
          </span>
        )}
        <ChevronDownIcon
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform duration-150 ml-auto",
            expanded && "rotate-180",
          )}
        />
      </div>

      {/* Unified Timeline */}
      <GridReveal open={expanded}>
        <div key={timelineGen}>
      {groupedTimeline.map((entry, i) => {
        const isLast = i === groupedTimeline.length - 1 && !docEditFlow.isActive;

        if (entry.kind === "reasoning-step") {
          const isActive = entry.isLastStep && flags.isReasoningStreaming;
          const isAnalysisNode = entry.stepIndex === 0 && metadata;
          const label = isAnalysisNode
            ? t("reasoning.queryAnalysis")
            : (entry.heading || t("reasoning.step"));
          const hasBody = entry.body.length > 0;
          // Analysis node (the first reasoning entry on a turn that ran the thinking phase)
          // used to render the per-category icon. With the category icon mapping retired,
          // all reasoning steps render the same neutral brain glyph.
          const icon = isActive
            ? ICON_SPINNER_SM
            : ICON_BRAIN;

          return (
            <TimelineNode
              key={entry.key}
              icon={icon}
              label={label}
              isActive={isActive}
              isLast={isLast}
              expandable={hasBody}
              defaultExpanded={isLiveMode}
              animated={isLiveMode}
            >
              {entry.body}
            </TimelineNode>
          );
        }

        // sub-agent lane
        if (entry.kind === "sub-agent") {
          return (
            <SubAgentLaneNode
              key={entry.key}
              entry={entry}
              isLast={isLast}
              isLiveMode={isLiveMode}
              t={t}
            />
          );
        }

        // tool-group
        // "Other tools" and "assistant" (native tools) groups render children as flat
        // timeline nodes without a parent heading — avoids redundant nesting when
        // sub-agent lanes already provide their own visual hierarchy.
        if (entry.groupKey === "__other__" || entry.groupKey === "assistant") {
          return entry.children
            .filter(child => !SUB_AGENT_TOOL_NAMES.has(child.toolName))
            .map((child, ci, arr) => {
              const childIsLast = isLast && ci === arr.length - 1;
              const childIcon = child.isRunning ? ICON_SPINNER_SM : ICON_DOT;
              const childLabel = child.toolTitle || child.toolName;
              return (
                <TimelineNode
                  key={child.key}
                  icon={childIcon}
                  label={childLabel}
                  isActive={child.isRunning}
                  isLast={childIsLast}
                  animated={isLiveMode}
                  plain
                />
              );
            });
        }

        // Approval group renders as a flat node (no expandable children)
        if (entry.groupKey === "__approval__") {
          const approvalChild = entry.children[0];
          const isAwaitingOrRunning = approvalChild?.isRunning ?? entry.isRunning;
          const approvalIcon = isAwaitingOrRunning ? ICON_SPINNER_MD : ICON_SAVE;
          const approvalLabel = isAwaitingOrRunning
            ? t("approval.awaiting_user")
            : t("approval.draft_approved");
          return (
            <TimelineNode
              key={entry.key}
              icon={approvalIcon}
              label={approvalLabel}
              isActive={isAwaitingOrRunning}
              isLast={isLast}
              animated={isLiveMode}
              plain
            />
          );
        }

        const groupIcon = entry.isWebSearch
            ? ICON_GLOBE
            : hasMcpServerIcon(entry.groupKey)
              ? <McpServerIcon name={entry.groupKey} />
              : resolveNativeGroupIcon(entry);

        const groupLabel = entry.isRunning
          ? (entry.runningText ?? entry.heading)
          : (entry.finishedText ?? entry.heading);

        return (
          <TimelineNode
            key={entry.key}
            icon={groupIcon}
            label={groupLabel}
            isActive={entry.isRunning}
            isLast={isLast}
            expandable
            defaultExpanded={isLiveMode}
            animated={isLiveMode}
            nested
          >
            {entry.children.map((child, ci) => {
              const isChildLast = ci === entry.children.length - 1;

              if (child.isWebSearch) {
                const wsIcon = child.isRunning
                  ? ICON_SPINNER_MD
                  : <WebSearchIcon toolName={child.toolName} argsText={child.argsText} />;
                return (
                  <TimelineNode
                    key={child.key}
                    icon={wsIcon}
                    label={truncateLabel(child.argsText || child.toolTitle || child.toolName)}
                    sublabel={child.progressMessage}
                    isActive={child.isRunning}
                    isLast={isChildLast}
                    animated={isLiveMode}
                    plain
                  />
                );
              }

              // request_commit_approval: show localized label, save icon, spinner while awaiting
              if (child.toolName === "request_commit_approval") {
                const approvalIcon = child.status === "running" ? ICON_SPINNER_MD : ICON_SAVE;
                const approvalLabel = child.status === "running"
                  ? t("approval.awaiting_user")
                  : t("approval.draft_approved");
                return (
                  <TimelineNode
                    key={child.key}
                    icon={approvalIcon}
                    label={approvalLabel}
                    isActive={child.isRunning}
                    isLast={isChildLast}
                    animated={isLiveMode}
                    plain
                  />
                );
              }

              const childIcon =
                child.status === "running" ? ICON_SPINNER_MD :
                child.status === "error"   ? ICON_X_ERROR    :
                                             ICON_CHECK_SUCCESS;
              // Args/result are debug-only — never expose them (or a chevron that
              // reveals nothing) in the production timeline. Detail expansion is
              // available only in debug mode, where the ToolGroup trace renders it.
              const showChildDetail = debugMode && !!(child.argsText || child.result);
              return (
                <TimelineNode
                  key={child.key}
                  icon={childIcon}
                  label={child.toolTitle || child.toolName}
                  sublabel={child.progressMessage}
                  isActive={child.isRunning}
                  isLast={isChildLast}
                  expandable={showChildDetail}
                  defaultExpanded={false}
                  animated={isLiveMode}
                  plain
                >
                  {showChildDetail ? <ToolCallDetail argsText={child.argsText} result={child.result} /> : undefined}
                </TimelineNode>
              );
            })}
          </TimelineNode>
        );
      })}
      {/* Document Generation Flow — renders as the last timeline group */}
      {docEditFlow.isActive && (
        <DocumentEditFlowGroup editFlow={docEditFlow} isLiveMode={isLiveMode} />
      )}
        </div>
      </GridReveal>
    </div>
  );
};

export const ProcessWindow = memo(ProcessWindowImpl);
ProcessWindow.displayName = "ProcessWindow";
